import { atom } from 'jotai';
import { createMutationAtom } from 'atoms/create-resource-atom';
import request from 'lib/request';
import type {
  StructuredPartyAddResponse,
  StructuredPartyLifecycleResponse,
  StructuredPartyRerunDocumentResponse,
  StructuredPartyRerunResponse,
} from 'types/api-responses';
import type { ApiPayloads } from 'types/api-payloads';
import type { StructuredPartiesListResponse } from '../types';

export const selectedPartyIndexAtom = atom<number | null>(null);

// ---------------------------------------------------------------------------
// List resource (GET /documents/:id/structured_parties)
//
// Module-level atoms (rather than `createResourceAtom`) so StructuringModule,
// PartiesOverview, and PartyDetail all share one piece of state and any of
// them can trigger a refetch that updates the others.
// ---------------------------------------------------------------------------

export const structuredPartiesListDataAtom =
  atom<StructuredPartiesListResponse | null>(null);
export const structuredPartiesListLoadingAtom = atom(false);
const listRequestSeqAtom = atom(0);

// Several surfaces reload the list at once (the run poll, the panels, the
// actions); only the most recent request writes the list and ends the loading.
// Each caller still gets its own response, or undefined when it failed.
export const fetchStructuredPartiesListAtom = atom(
  null,
  async (
    get,
    set,
    href: string | undefined
  ): Promise<StructuredPartiesListResponse | undefined> => {
    if (!href) return undefined;
    const seq = get(listRequestSeqAtom) + 1;
    set(listRequestSeqAtom, seq);
    /** True while this call is still the most recent list request. */
    const isLatest = () => get(listRequestSeqAtom) === seq;
    set(structuredPartiesListLoadingAtom, true);
    try {
      const response = await request.get<StructuredPartiesListResponse>(href);
      if ('error' in response) {
        console.error('Failed to fetch structured parties:', response.error);
        return undefined;
      }
      if (isLatest()) set(structuredPartiesListDataAtom, response);
      return response;
    } catch (error) {
      console.error('Failed to fetch structured parties:', error);
      return undefined;
    } finally {
      if (isLatest()) set(structuredPartiesListLoadingAtom, false);
    }
  }
);

// Identifies this tab's own actions in the pushes they cause. Fresh per page
// load: a duplicated tab copies sessionStorage, so it can't live there.
export const CLIENT_SESSION_ID =
  globalThis.crypto?.randomUUID?.() ??
  `client-${Date.now()}-${Math.round(Math.random() * 1e9)}`;

// ---------------------------------------------------------------------------
// Mutation atoms (one per lifecycle action)
// ---------------------------------------------------------------------------

export const confirmSuggestionAtom = createMutationAtom<
  StructuredPartyLifecycleResponse,
  'structuredPartyConfirmation'
>();

export const revertSuggestionAtom = createMutationAtom<
  StructuredPartyLifecycleResponse,
  'structuredPartyRevert'
>();

export const canonizePartyAtom = createMutationAtom<
  StructuredPartyLifecycleResponse,
  'structuredPartyCanonization'
>();

export const removePartyAtom = createMutationAtom<
  StructuredPartyLifecycleResponse,
  'structuredPartyRemoval'
>();

export const restorePartyAtom = createMutationAtom<
  StructuredPartyLifecycleResponse,
  'structuredPartyRestoration'
>();

export const rerunPartyAtom = createMutationAtom<
  StructuredPartyRerunResponse,
  'structuredPartyRerun'
>();

export const rerunDocumentAtom = createMutationAtom<
  StructuredPartyRerunDocumentResponse,
  'structuredPartyRerun'
>();

export const saveUserSelectionAtom = createMutationAtom<
  StructuredPartyLifecycleResponse,
  'structuredPartyMatchCandidate'
>();

export const addPartyAtom = createMutationAtom<
  StructuredPartyAddResponse,
  'structuredPartyCreate'
>();

export const updatePartyDetailsAtom = createMutationAtom<
  StructuredPartyLifecycleResponse,
  'structuredPartyUpdate'
>();

/**
 * The editor's changed fields, as the party details PATCH takes them.
 * @param {object} changes - The edited fields, without the client session id
 * @param {number | undefined} anchorEntityId - The anchor the editor was showing; the server
 *   refuses the save once the party's anchor is another
 */
export const partyDetailsData = (
  changes: Omit<
    ApiPayloads['structuredPartyUpdate']['payload'],
    'client_session_id' | 'anchor_entity_id'
  >,
  anchorEntityId: number | undefined
): ApiPayloads['structuredPartyUpdate']['payload'] => ({
  name: changes.name,
  role: changes.role,
  addresses_attributes: changes.addresses_attributes,
  phone_numbers_attributes: changes.phone_numbers_attributes,
  client_session_id: CLIENT_SESSION_ID,
  anchor_entity_id: anchorEntityId,
});

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/**
 * URL of a structured party, scoped by its kind, with an optional suffix.
 * @param {number} documentId - The document the party is staged on
 * @param {number} partyId - The staged party's id
 * @param {'imported_entity' | 'user_added'} partyKind - Which kind of party the id refers to
 * @param {string} suffix - A sub-path appended to the party URL
 */
export const partyUrl = (
  documentId: number,
  partyId: number,
  partyKind: 'imported_entity' | 'user_added',
  suffix = ''
) =>
  `/documents/${documentId}/structured_parties/${partyId}${suffix}?kind=${partyKind}`;

/**
 * URL to POST a new user-added party to a document.
 * @param {number} documentId - The document to add the party to
 */
export const addPartyUrl = (documentId: number) =>
  `/documents/${documentId}/structured_parties`;
